import { APIProvider } from "@vis.gl/react-google-maps";
import { FC } from "react";

import InlineError from "src/components/Errors/InlineError.tsx";
import { DynamicViewData } from "src/dynamic-view-renderer/DynamicViewRenderer.typings.ts";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import {
  CustomerCasesTable,
  DashboardRowConfiguration,
  DashboardWidgetConfiguration,
} from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types.ts";
import { AlertsAndExecutionsWidget } from "src/dynamic-view-renderer/Widgets/AlertsAndExecutionsWidget/AlertsAndExecutionsWidget.tsx";
import AlertWorkflowWidget from "src/dynamic-view-renderer/Widgets/AlertWorkflowWidget/AlertWorkflowWidget.tsx";
import ChronologyWidget from "src/dynamic-view-renderer/Widgets/ChronologyWidget.tsx";
import CustomerOpenAlertsSummaryWidget from "src/dynamic-view-renderer/Widgets/CustomerOpenAlertsSummaryWidget/CustomerOpenAlertsSummaryWidget.tsx";
import {
  ElementListWidget,
  ElementListWidgetConfiguration,
} from "src/dynamic-view-renderer/Widgets/ElementListWidget/ElementListWidget.tsx";
import GoogleMapsWidget, {
  GoogleMapsWidgetConfiguration,
} from "src/dynamic-view-renderer/Widgets/GoogleMapsWidget/GoogleMapsWidget.tsx";
import GoogleStreetViewWidget, {
  GoogleStreetViewWidgetConfiguration,
} from "src/dynamic-view-renderer/Widgets/GoogleMapsWidget/GoogleStreetViewWidget.tsx";
import { RelatedWidgetConfiguration } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import RulesWidget from "src/dynamic-view-renderer/Widgets/RulesWidget/RulesWidget.tsx";
import { WidgetDefinition } from "src/dynamic-view-renderer/Widgets/Widget.typings.ts";
import WidgetContainer from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useGetGoogleMapsApiKeyQuery } from "src/graphql/anti-fraud/queries/googleMaps/GetGoogleMapsApiKey.ts";
import { useFormatPropertyValue } from "src/hooks/property/useFormatProperty.ts";
import { useT } from "src/internationalization";
import { mapElementListToElementListWidgetData } from "src/mappers/elementListWidget.mappers.ts";

import CaseAlertsWidget from "../CaseAlertsWidget/CaseAlertsWidget.tsx";

import CustomerCasesWidget from "./Customer/CustomerCasesWidget.tsx";
import { ExecutionDetailWidget } from "./ExecutionsWidget/ExecutionDetailWidget";
import { ExecutionsWidget } from "./ExecutionsWidget/ExecutionsWidget.tsx";
import RelatedCasesWidget from "./RelatedCases/RelatedCasesWidget.tsx";
import WidgetSchema from "./WidgetSchema/WidgetSchema.tsx";

export type WidgetRendererProps = {
  data: DynamicViewData;
  widgetConfig: DashboardWidgetConfiguration;
  rowConfiguration: DashboardRowConfiguration;
};

/** UI schema may put options under `configuration.customConfiguration` (preferred) or flat on `configuration`. */
const getElementListWidgetConfiguration = (
  widgetDefinition: Pick<WidgetDefinition, "configuration">
): ElementListWidgetConfiguration | undefined => {
  const cfg = widgetDefinition.configuration;
  if (!cfg) return undefined;

  const custom = cfg.customConfiguration as ElementListWidgetConfiguration | undefined;
  const root = cfg as Record<string, unknown>;

  const elementListCode =
    custom?.elementListCode ??
    (typeof root.elementListCode === "string" ? root.elementListCode : undefined);

  const columns = custom?.columns ?? (typeof root.columns === "number" ? root.columns : undefined);

  return { elementListCode, columns };
};

const WidgetRenderer: FC<WidgetRendererProps> = ({ rowConfiguration, widgetConfig, data }) => {
  const uiSchema = useDynamicViewSchema();

  const widgetDefinitions = uiSchema.widgetDefinitions;
  const t = useT();

  const widgetDefinition = widgetDefinitions.find(
    (definition) => definition.code === widgetConfig.widgetCode
  );
  const formatPropertyValue = useFormatPropertyValue();

  const { data: apiKey } = useGetGoogleMapsApiKeyQuery();

  if (!widgetDefinition) {
    console.warn("There is no widget definition with code: ", widgetConfig.widgetCode);

    return (
      <WidgetContainer widgetConfig={widgetConfig} rowConfiguration={rowConfiguration}>
        There is no widget definition with code: {widgetConfig.widgetCode}
      </WidgetContainer>
    );
  }

  const widgetContainerAttributes = {
    widgetConfig,
    rowConfiguration,
    widgetDefinition,
  };

  switch (widgetDefinition?.type) {
    case "customerCases":
      return (
        <CustomerCasesWidget
          {...widgetContainerAttributes}
          widgetDefinition={
            widgetContainerAttributes.widgetDefinition as WidgetDefinition<{
              customerCasesTable: CustomerCasesTable;
            }>
          }
          data={data.widgetsData.customerCases}
        />
      );
    case "rules":
      if (data.widgetsData.origin === "manual") {
        return null;
      }
      return (
        <RulesWidget
          {...widgetContainerAttributes}
          configuration={
            widgetDefinition.configuration?.customConfiguration as RelatedWidgetConfiguration
          }
          data={data.widgetsData.rules}
        />
      );
    case "chronology":
      return <ChronologyWidget {...widgetContainerAttributes} data={data.widgetsData.chronology} />;

    case "table": {
      //TODO: validate widgetDefinition.configuration?.customConfiguration
      return data.widgetsData.relatedCases ? (
        <WidgetContainer {...widgetContainerAttributes}>
          <RelatedCasesWidget
            data={data.widgetsData.relatedCases}
            configuration={
              widgetDefinition.configuration?.customConfiguration as RelatedWidgetConfiguration
            }
          />
        </WidgetContainer>
      ) : (
        <InlineError message={t("general.table.empty")} />
      );
    }

    case "alertWorkflow":
      return (
        <AlertWorkflowWidget {...widgetContainerAttributes} data={data.widgetsData.alertWorkflow} />
      );

    case "executions":
      return <ExecutionsWidget {...widgetContainerAttributes} data={data.widgetsData.executions} />;

    case "alertsAndExecutions":
      return (
        <AlertsAndExecutionsWidget
          {...widgetContainerAttributes}
          data={data.widgetsData.alertsAndExecutions}
        />
      );

    case "executionDetails":
      return (
        <ExecutionDetailWidget
          {...widgetContainerAttributes}
          data={data.widgetsData.executionDetails}
        />
      );
    case "elementList": {
      const configuration = getElementListWidgetConfiguration(widgetDefinition);
      const selectedElementList = configuration?.elementListCode
        ? data.widgetsData.elementLists?.find((elementList) => {
            return elementList.code === configuration.elementListCode;
          })
        : data.widgetsData.elementLists?.[0];

      return (
        <ElementListWidget
          {...widgetContainerAttributes}
          data={mapElementListToElementListWidgetData(selectedElementList, formatPropertyValue)}
          configuration={configuration}
        />
      );
    }

    case "googleMaps":
      return (
        <GoogleMapsWidget
          {...widgetContainerAttributes}
          configuration={
            widgetDefinition.configuration?.customConfiguration as
              | GoogleMapsWidgetConfiguration
              | undefined
          }
          data={data.widgetsData.googleMaps}
          apiKey={apiKey}
        />
      );

    case "googleStreetView":
      if (!apiKey) {
        return (
          <WidgetContainer {...widgetContainerAttributes}>
            <div className="text-supportError">Google Maps API key is required</div>
          </WidgetContainer>
        );
      }
      return (
        <APIProvider apiKey={apiKey}>
          <GoogleStreetViewWidget
            {...widgetContainerAttributes}
            data={data.widgetsData.googleStreetView}
            configuration={
              widgetDefinition.configuration?.customConfiguration as
                | GoogleStreetViewWidgetConfiguration
                | undefined
            }
          />
        </APIProvider>
      );

    case "caseAlerts":
      return <CaseAlertsWidget {...widgetContainerAttributes} data={data.widgetsData.caseAlerts} />;

    case "customerOpenAlertsSummary":
      return (
        <CustomerOpenAlertsSummaryWidget
          {...widgetContainerAttributes}
          data={data.widgetsData.customerOpenAlertsSummary}
        />
      );

    default:
      return <WidgetSchema {...widgetContainerAttributes} data={data.widgetsData.basic} />;
  }
};

export default WidgetRenderer;
